import { BaseTextFieldProps, TextField } from '@mui/material';
import { ChangeEvent, useId } from 'react';
import { FieldErrors, get, useController, useFormContext } from 'react-hook-form';
import { z } from 'zod';

export interface ITextInputProps extends BaseTextFieldProps {
	name: string;
	label?: string;
	required?: boolean;
	formHelperText?: string;
	dataTestId?: string;
	maxLength?: number;
	pattern?: string;
	placeholder?: string;
	disabled?: boolean;
	enforceAlphaNumeric?: boolean;
	autoComplete?: string;
	multiline?: boolean;
	rows?: number;
	inputProps?: BaseTextFieldProps['inputProps'];
}

export const textInputSchema = z.string();

type ValueOf<T> = T[keyof T];

const ssn = z.coerce.string().regex(/^\d{3}-\d{2}-\d{4}$|^\d{9}$/, 'Valid Social Security Number Required');

export const TextInputSchema = ((options) => {
	return {
		options,
		create: (fieldName: string, validation: ValueOf<typeof options>) => {
			return z.object({ [fieldName]: validation });
		},
	};
})({
	// validations:
	default: z.string().min(1, 'Required'),
	notRequired: z.string(),
	email: z.string().email(),
	emailBasic: z
		.string()
		.email()
		.regex(/^(?!.*\s)(?!.*,,)(?!.*\.\.)(?!.*@.*@)[^,]+$/, 'Please enter a valid email.'),
	ssn,
	ssnNotRequired: ssn.or(z.literal('')),
	zip: z.coerce.string().regex(/^\d{5}$|^\d{5}-\d{4}$/, 'Valid Zip Code Required'),
});

export default function TextInput({
	label,
	name,
	required = true,
	maxLength,
	placeholder,
	disabled = false,
	dataTestId,
	enforceAlphaNumeric = true,
	autoComplete = 'off',
	multiline = false,
	rows,
	inputProps,
	...props
}: ITextInputProps) {
	const { control } = useFormContext();
	const fieldId = useId();
	const testId = dataTestId ? `${dataTestId}_${fieldId}` : 'form_field_text_input';

	const {
		field,
		formState: { errors },
	} = useController({ name, control });

	const error = get(errors, name) as FieldErrors;

	const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const value = e.currentTarget.value;
		if (!enforceAlphaNumeric) field.onChange(e.target.value);

		if (value.match(/^[a-zA-Z0-9\-/ ]*$/)) {
			field.onChange(e.target.value);
		}
	};

	return (
		<TextField
			{...field}
			fullWidth
			label={label}
			disabled={disabled}
			error={Boolean(error)}
			helperText={<>{error?.message}</>}
			required={required}
			placeholder={placeholder}
			onChange={(e) => handleChange(e)}
			autoComplete={autoComplete}
			inputProps={{
				maxLength,
				'data-test-id': testId,
				...inputProps,
			}}
			multiline={multiline}
			rows={rows}
			sx={{
				'& .MuiInputLabel-root': {
					color: error ? 'error.dark' : undefined,
				},
			}}
			{...props}
		/>
	);
}
